import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, CheckSquare, FolderKanban, Calendar, Briefcase, TrendingUp,
  Wallet, HardDrive,
  Share2, PenSquare, Files, Settings, KeyRound, UserCircle, Bot, Store,
  ChevronRight, ChevronDown, Map, Link2, Gauge,
  BrainCircuit, Sparkles, Activity, Lightbulb,
  BookOpen, Trophy,
  Globe, Mail, Search, CalendarClock, Megaphone, MessagesSquare,
} from 'lucide-react';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useAuth } from '../hooks/useAuth';

// MAIN group. Owner directive 2026-09-30: "Agents" is nested under "Autonomy
// Control" (same parent/child pattern as Dashboard → Tasks/Projects), and the
// dashboard utility apps moved into the collapsible "Dashboard apps" group
// below. Knowledge Base is rendered as the last item in the rail.
const navItems = [
  // Owner directive 2026-09-30: "Command Center" sits at the TOP of the list.
  { to: '/app', label: 'Command Center', icon: LayoutDashboard, end: true, agentId: 'nav.command-center' },
  { to: '/app/opportunity-scanner', label: 'Opportunity Scanner', icon: Lightbulb, module: 'automation', agentId: 'nav.opportunity-scanner' },
  // Owner 2026-10-19: ONE messaging module. SAIK Chat is no longer a separate
  // sidebar entry — it lives inside Messenger as the "Assistant" tab, and
  // /app/chat redirects to /app/messenger.
  { to: '/app/messenger', label: 'Messenger', icon: MessagesSquare, agentId: 'nav.messenger' },
];

// Owner directive 2026-09-30: a collapsible "Dashboard apps" group (collapsed on
// load) holding the core dashboard utility apps. Each item keeps its own module
// gate + ownerOnly rule, exactly as it behaved while in MAIN.
const dashboardAppsGroup = {
  section: 'Dashboard apps', defaultOpen: false, items: [
    // Tasks & Projects joined this group (owner directive 2026-09-30).
    { to: '/app/tasks', label: 'Tasks', icon: CheckSquare, agentId: 'nav.tasks' },
    { to: '/app/projects', label: 'Projects', icon: FolderKanban, agentId: 'nav.projects' },
    { to: '/app/calendar', label: 'Calendar', icon: Calendar, agentId: 'nav.calendar' },
    { to: '/app/financials', label: 'Financials', icon: Wallet, module: 'financials', agentId: 'nav.financials' },
    { to: '/app/memory', label: 'Memory', icon: HardDrive, agentId: 'nav.memory' },
    { to: '/app/email/inbox', label: 'Email', icon: Mail, agentId: 'nav.email' },
    { to: '/app/files', label: 'Files', icon: Files, agentId: 'nav.files' },
    { to: '/app/sites', label: 'My Sites', icon: Globe, agentId: 'nav.sites' },
  ],
};
const moduleItems = [
  // Owner 2026-10-01: Agents is nested INSIDE a collapsible "Autonomy" group
  // (with Autonomy Control), and the group sits right under the Chat module.
  { section: 'Autonomy', module: 'automation', defaultOpen: false, items: [
    { to: '/app/ops/automation', label: 'Autonomy Control', icon: Bot, agentId: 'nav.autonomy' },
    { to: '/app/agents', label: 'Agent Jobs', icon: Bot, agentId: 'nav.agents' },
    { to: '/app/leaderboard', label: 'Leaderboard', icon: Trophy, agentId: 'nav.autonomy' },
    { to: '/app/prompts', label: 'Prompts', icon: BookOpen, agentId: 'nav.prompts', ownerOnly: true },
  ]},
  dashboardAppsGroup,
  { section: 'Job Finder', module: 'jobbot', defaultOpen: false, items: [
    { to: '/app/jobbot', label: 'Overview', icon: Briefcase },
    { to: '/app/jobbot/profile', label: 'Profile', icon: UserCircle, agentId: 'nav.jobbot.profile' },
    { to: '/app/jobbot/searches', label: 'Searches', icon: Search, agentId: 'nav.jobbot.searches' },
    { to: '/app/jobbot/interviews', label: 'Interviews', icon: CalendarClock, agentId: 'nav.jobbot.interviews' },
    { to: '/app/jobbot/leads', label: 'Leads', icon: Briefcase },
    { to: '/app/jobbot/applications', label: 'Applications', icon: Briefcase },
    { to: '/app/jobbot/resumes', label: 'Resumes', icon: Briefcase },
  ]},
  { section: 'Trading', module: 'trading', defaultOpen: false, items: [
    { to: '/app/trading', label: 'Dashboard', icon: TrendingUp },
    { to: '/app/trading/orders', label: 'Orders', icon: TrendingUp },
    { to: '/app/trading/positions', label: 'Positions', icon: TrendingUp },
    { to: '/app/trading/history', label: 'History', icon: TrendingUp },
    { to: '/app/trading/performance', label: 'Performance', icon: TrendingUp },
  ]},
  { section: 'Social', module: 'social', defaultOpen: false, items: [
    { to: '/app/social', label: 'Manager', icon: Share2 },
    { to: '/app/social/accounts', label: 'Accounts', icon: Share2 },
    { to: '/app/social/content', label: 'Content', icon: Share2 },
    { to: '/app/social/composer', label: 'Composer', icon: Share2 },
    { to: '/app/social/analytics', label: 'Analytics', icon: Share2 },
  ]},
  { section: 'Content', module: 'content', defaultOpen: false, items: [
    { to: '/app/content', label: 'Studio', icon: PenSquare },
    { to: '/app/content/projects', label: 'Projects', icon: PenSquare },
    { to: '/app/content/jobs', label: 'Jobs', icon: PenSquare },
    { to: '/app/content/assets', label: 'Assets', icon: PenSquare },
  ]},
  { section: 'Marketplace', module: 'marketplace', defaultOpen: false, items: [
    { to: '/app/marketplace', label: 'Accounts', icon: Store },
    { to: '/app/marketplace/sales', label: 'Sales & Distribution', icon: TrendingUp },
  ]},
  { section: 'Marketing', module: 'marketing', defaultOpen: false, items: [
    { to: '/app/marketing', label: 'Campaigns & ROAS', icon: Megaphone },
  ]},
];



const roadmapItem = { to: '/app/roadmap', label: 'Roadmap', icon: Map };

interface SidebarProps { open: boolean; onClose: () => void; }

export default function Sidebar({ open, onClose }: SidebarProps) {
  const { enabled } = useModuleAccess();
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const navigate = useNavigate();
  const followLink = (event: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    // Route before closing the transformed mobile drawer. Some mobile browsers
    // can drop the anchor's default navigation when the drawer changes state
    // during the same tap.
    event.preventDefault();
    navigate(to);
    onClose();
  };
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(
    () => Object.fromEntries(moduleItems.map(g => [g.section, g.defaultOpen ?? false]))
  );
  // Owner 2026-10-20 (correction 02:13): the owner SEES every module, but the
  // sections stay collapsed by default — do not auto-expand them.
  const toggleSection = (section: string) => setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));

  return (
    <>
      {open && <div className="fixed inset-0 z-30 bg-slate-900/60 lg:hidden" onClick={onClose} aria-hidden="true" />}
      <aside
        role="navigation"
        aria-label="Main navigation"
        className={`glass-rail fixed inset-y-0 left-0 z-40 w-64 transform overflow-y-auto transition-transform lg:static lg:h-full lg:min-h-0 lg:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="flex h-16 items-center gap-2 border-b border-slate-800 px-6">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-purple-600 shadow-lg shadow-brand-500/20">
            <svg width="16" height="16" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="sidebar-logo" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#c7d2fe" />
                  <stop offset="1" stopColor="#e9d5ff" />
                </linearGradient>
              </defs>
              <path d="M16 40 L20 24 L26 32 L32 20 L38 32 L44 24 L48 40 Z" fill="url(#sidebar-logo)" opacity="0.98" />
              <path d="M16 40 L48 40 L46 44 L18 44 Z" fill="white" opacity="0.85" />
              <circle cx="32" cy="36" r="3" fill="url(#sidebar-logo)" />
            </svg>
          </div>
          <span className="text-lg font-bold text-slate-100">SAIK</span>
        </div>
        <nav className="flex flex-col gap-1 px-3 py-4">
          <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Main</p>
          {navItems.filter(item => (!(item as any).module || enabled((item as any).module) || isOwner) && (!(item as any).ownerOnly || isOwner)).map((item) => {
            const Icon = item.icon;
            const hasChildren = !!(item as any).children;
            const childItems = (item as any).children || [];
            const childKey = `nav-${item.label}`;
            if (hasChildren) {
              return (
                <div key={item.to}>
                  <NavLink to={item.to} end={item.end} onClick={(event) => followLink(event, item.to)} data-agent-id={item.agentId}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                        isActive ? 'bg-brand-600/20 text-brand-400' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                      }`
                    }>
                    <Icon size={18} aria-hidden="true" /> {item.label}
                  </NavLink>
                  <div className="mt-0.5 ml-4 flex flex-col gap-0.5 border-l border-slate-800 pl-3">
                    {childItems.map((child: any) => {
                      const ChildIcon = child.icon;
                      return (
                        <NavLink key={child.to} to={child.to} onClick={(event) => followLink(event, child.to)} data-agent-id={child.agentId}
                          className={({ isActive }) =>
                            `flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors ${
                              isActive ? 'text-brand-400 font-medium' : 'text-slate-500 hover:bg-slate-800 hover:text-slate-300'
                            }`
                          }>
                          <ChildIcon size={15} aria-hidden="true" /> {child.label}
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              );
            }
            return (
              <NavLink key={item.to} to={item.to} end={item.end} onClick={(event) => followLink(event, item.to)} data-agent-id={item.agentId}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? 'bg-brand-600/20 text-brand-400' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                  }`
                }>
                <Icon size={18} aria-hidden="true" /> {item.label}
              </NavLink>
            );
          })}
          {moduleItems
            .map((group: any) => ({
              group,
              visible: group.items.filter((item: any) => (!item.module || enabled(item.module) || isOwner) && (!item.ownerOnly || isOwner)),
            }))
            .filter(({ group, visible }: any) => (!group.module || enabled(group.module) || isOwner) && visible.length > 0)
            .map(({ group, visible }: any) => (
            <div key={group.section}>
              <button onClick={() => toggleSection(group.section)}
                className="flex w-full items-center gap-2 px-3 pb-2 pt-6 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-500 rounded"
                aria-expanded={openSections[group.section]}
                aria-controls={`nav-section-${group.section.toLowerCase()}`}
              >
                {openSections[group.section] ? <ChevronDown size={12} aria-hidden="true" /> : <ChevronRight size={12} aria-hidden="true" />}
                {group.section}
              </button>
              {openSections[group.section] && <div id={`nav-section-${group.section.toLowerCase()}`} className="flex flex-col gap-1">{visible.map((item: any) => {
                const Icon = item.icon;
                return (
                  <NavLink key={item.to} to={item.to}
                    end={item.to === '/app/jobbot' || item.to === '/app/trading' || item.to === '/app/social' || item.to === '/app/content' || item.to === '/app/marketplace' || item.to === '/app/marketing'}
                    onClick={(event) => followLink(event, item.to)}
                    data-agent-id={item.agentId}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                        isActive ? 'bg-brand-600/20 text-brand-400' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                      }`
                    }>
                    <Icon size={18} aria-hidden="true" /> {item.label}
                  </NavLink>
                );
              })}</div>}
            </div>
          ))}
          {/* Roadmap */}
          {enabled('roadmap') && <><p className="px-3 pb-2 pt-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Planning</p>
          <NavLink to={roadmapItem.to} onClick={(event) => followLink(event, roadmapItem.to)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? 'bg-brand-600/20 text-brand-400' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
              }`
            }>
            <roadmapItem.icon size={18} aria-hidden="true" /> {roadmapItem.label}
          </NavLink></>}
          {/* Owner directive 2026-09-30: Knowledge Base is the LAST item in the rail. */}
          <NavLink to="/app/knowledge-base" onClick={(event) => followLink(event, '/app/knowledge-base')} data-agent-id="nav.knowledge-base"
            className={({ isActive }) =>
              `mt-2 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? 'bg-brand-600/20 text-brand-400' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
              }`
            }>
            <BookOpen size={18} aria-hidden="true" /> Knowledge Base
          </NavLink>
        </nav>
      </aside>
    </>
  );
}
